import { h, createRef } from "preact";
import { useEffect, useLayoutEffect, useState } from "preact/hooks";
import { createPortal } from "preact/compat";

import Alert from "./Alert.jsx";
import Button from "./Button.jsx";
import LinearLoadingIndicator from "./LinearLoadingIndicator.jsx";
import CopyableLink from "./CopyableLink.jsx";
import TextField from "./TextField.jsx";
import Overlay from "./Overlay.jsx";

import i18n from "../i18n";
import talentsApi from "../api/talents";
import { sendError } from "../errors";
import Icon from "./Icon.jsx";

export default function ShareProfileDialog({ talent, onClose, onOpen }) {
  const dialogRef = createRef();
  const overlayRef = createRef();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState(null);
  const [links, setLinks] = useState([]);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSending(true);
    setFormErrors(null);

    try {
      const result = await talentsApi.share(talent.id, name, password);
      setSending(false);
      setName("");
      setPassword("");
      setLinks([...links, { ...result, highlight: true }]);
    } catch (err) {
      setSending(false);
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        sendError(err);
        setError("Could not create share link. Please try again later.");
      }
    }
  };

  useEffect(async () => {
    setLoading(true);
    try {
      setLinks(await talentsApi.listShareLinks(talent.id));
    } catch (err) {
      sendError(err);
      setError("Could not load share links. Please try again later.");
    }
    setLoading(false);
  }, []);

  useLayoutEffect(() => {
    // TODO: Some of this stuff should belong to Overlay and some should go into a
    //       general Dialog component
    const overlay = overlayRef.current.base;
    overlay.classList.add("overlay--open");
    const wrapper = dialogRef.current;
    wrapper.classList.add("dialog--open");
    wrapper.addEventListener(
      "animationend",
      () => {
        onOpen && onOpen();
      },
      { once: true } // Does not work in IE, but who cares
    );

    return () => {
      overlay.classList.add("overlay--exit");
      wrapper.classList.add("dialog--exit");

      return new Promise(function (resolve) {
        wrapper.addEventListener("animationend", resolve);
      });
    };
  }, []);

  return createPortal(
    <Overlay onClose={onClose} ref={overlayRef}>
      <section class="dialog dialog--md card" ref={dialogRef}>
        {(loading || sending) && <LinearLoadingIndicator />}
        <h1 class="dialog__title t-h6 t-h6--margin">
          {i18n.t("component.share_profile_dialog.title", {
            name: talent.data.name,
          })}
          <Icon class="dialog__closer" onClick={onClose}>
            close
          </Icon>
        </h1>

        {error && (
          <Alert error class="mb-16">
            {error}
          </Alert>
        )}

        {links &&
          links.length > 0 &&
          links.map((link) => (
            <CopyableLink
              highlight={link.highlight}
              name={link.name}
              value={`${location.origin}/app/shared/${encodeURI(
                link.link_id
              )}?token=${encodeURIComponent(link.token)}`}
            />
          ))}

        <form action="#" method="post" onSubmit={handleSubmit}>
          <h4 class="t-subtitle1 t-subtitle1--margin">
            {i18n.t("component.share_profile_dialog.form_title")}
          </h4>
          <TextField
            name="name"
            label={i18n.t("component.share_profile_dialog.labels.name")}
            error={formErrors && formErrors.name}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            name="password"
            label={i18n.t("component.share_profile_dialog.labels.password")}
            error={formErrors && formErrors.password}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            revealable
          />
          <div class="dialog__buttonRow">
            <Button type="submit" disabled={sending}>
              {i18n.t("component.share_profile_dialog.submit_button")}
            </Button>
            <Button text onClick={onClose}>
              {i18n.t("actions.close")}
            </Button>
          </div>
        </form>
      </section>
    </Overlay>,
    document.getElementById("modals")
  );
}
